import { useQuery } from "@tanstack/react-query"
import type {
  AdminCustomerRecord,
  AdminFreeProduct,
  AdminTouch,
  SignupField,
} from "@ternary/contracts"
import {
  Avatar,
  Badge,
  breakable,
  Card,
  DescriptionList,
  EmptyState,
  IconButton,
  PageHeader,
  Skeleton,
  Timeline,
  useToast,
} from "@ternary/ui"
import { type ReactNode, useEffect, useRef } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import { AdminApiError, loadAdminCustomer, loadAdminProducts } from "../api.js"
import { ChannelBadge } from "../components/channel-badge.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"
import { type CustomerRecordState, discordAvatarSrc, fullName, humanizeKey } from "../customer.js"
import { compareJourneyEvents, describeJourneyEvent } from "../journey.js"

// The admin's copy is English, so its dates are too, as on the list; the time zone is the
// viewer's. The full timestamp, zone included, is each time's title.
const LOCALE = "en-US"
const DATE_TIME = new Intl.DateTimeFormat(LOCALE, { dateStyle: "medium", timeStyle: "short" })
const FULL_TIME = new Intl.DateTimeFormat(LOCALE, { dateStyle: "full", timeStyle: "long" })
// A date answer is a calendar day, the same day wherever the admin is.
const DAY = new Intl.DateTimeFormat(LOCALE, { dateStyle: "medium", timeZone: "UTC" })
const REGION = new Intl.DisplayNames(LOCALE, { type: "region" })
// Every UTM tag a touch can carry. The Acquisition card's Source, Medium and Campaign rows are the
// first touch's, so the first touch lists only the rest; a last touch of its own lists all five.
const UTM_KEYS = ["source", "medium", "campaign", "term", "content"] as const
const FIRST_TOUCH_UTM_KEYS = ["term", "content"] as const

/** The list's query from history state, such as "?channel=meta". Anything else is ignored. */
function listSearch(state: unknown) {
  const value = (state as Partial<CustomerRecordState> | null)?.customerList
  return typeof value === "string" && value.startsWith("?") ? value : ""
}

function When({ iso }: { iso: string }) {
  const date = new Date(iso)
  return (
    <time className="admin-nowrap" dateTime={iso} title={FULL_TIME.format(date)}>
      {DATE_TIME.format(date)}
    </time>
  )
}

const none = <span className="admin-note">None</span>

/**
 * A landing page's URL on one line, cut short with an ellipsis: the whole URL is its title, and
 * Copy takes it whole. The API stored it without click IDs. `touch` names the Copy button
 * ("first touch"), so a record's two read apart.
 */
function LandingUrl({ touch, url }: { touch: string; url: string }) {
  const { toast } = useToast()

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      toast({ message: "Landing page URL copied.", tone: "success" })
    } catch {
      toast({ message: `The URL could not be copied: ${url}`, tone: "error" })
    }
  }

  return (
    <span className="admin-url">
      <span className="admin-url-text" title={url}>
        {url}
      </span>
      <IconButton
        icon={<NavIcon name="copy" />}
        label={`Copy ${touch} landing page URL`}
        onClick={() => void copy()}
        size="sm"
      />
    </span>
  )
}

function countryName(code: string) {
  try {
    return REGION.of(code) ?? code
  } catch {
    return code
  }
}

/**
 * Each answer key's signup field, for its label: the customer's own products first, in their
 * order, then the organization's others.
 */
function answerFields(record: AdminCustomerRecord, products: AdminFreeProduct[]) {
  const mine = record.products.map(({ id }) => id)
  const rank = (product: AdminFreeProduct) =>
    mine.includes(product.id) ? mine.indexOf(product.id) : mine.length
  const fields = new Map<string, SignupField>()
  for (const product of [...products].sort((a, b) => rank(a) - rank(b))) {
    for (const field of product.signupFields) {
      if (!fields.has(field.key)) fields.set(field.key, field)
    }
  }
  return fields
}

/** A multiselect's stored JSON array of choices, or null for any other value. */
function choices(value: string) {
  if (!value.startsWith("[")) return null
  try {
    const parsed: unknown = JSON.parse(value)
    return Array.isArray(parsed) && parsed.every((choice) => typeof choice === "string")
      ? parsed
      : null
  } catch {
    return null
  }
}

/**
 * An answer as stored (Task 14): a checkbox "true" or "false", a multiselect its JSON array, a
 * date its day. Without its field (an archived product's, or the products did not load) the
 * value is read as the kind it looks like.
 */
function answerText(field: SignupField | undefined, value: string): ReactNode {
  const kind = field?.kind
  if ((kind === "checkbox" || !kind) && (value === "true" || value === "false")) {
    return value === "true" ? "Yes" : "No"
  }
  const list = kind === "multiselect" || !kind ? choices(value) : null
  if (list) return list.join(", ")
  const day = new Date(`${value}T00:00:00Z`)
  if (
    (kind === "date" || !kind) &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(day.getTime())
  ) {
    return <time dateTime={value}>{DAY.format(day)}</time>
  }
  return value
}

function Answers({ record }: { record: AdminCustomerRecord }) {
  const keys = Object.keys(record.answers)
  const products = useQuery({
    enabled: keys.length > 0,
    queryFn: ({ signal }) => loadAdminProducts({ signal }),
    queryKey: ["admin-products"],
  })
  if (keys.length === 0) return <p className="admin-note">No answers.</p>
  if (products.isPending) return <Skeleton lines={keys.length} />
  const fields = answerFields(record, products.data?.freeProducts ?? [])
  const ordered = [
    ...[...fields.keys()].filter((key) => Object.hasOwn(record.answers, key)),
    ...keys.filter((key) => !fields.has(key)),
  ]
  return (
    <div className="admin-stack">
      {products.isError ? (
        <p className="admin-note">
          The field labels could not load, so the answers show their keys.
        </p>
      ) : null}
      {/* Each question over its answer: a question can be a long sentence. */}
      <DescriptionList
        items={ordered.map((key) => {
          const field = fields.get(key)
          return {
            // A key no product asks any more: a readable label, with the stored key in its title.
            term: field ? field.label : <span title={key}>{humanizeKey(key)}</span>,
            value: answerText(field, record.answers[key] ?? ""),
          }
        })}
        stacked
      />
    </div>
  )
}

/**
 * One touch in full. The API stored its URL and referrer without click IDs, so they show as is. Its
 * UTM tags row lists `utmKeys` (the first touch: only term and content, which the Acquisition
 * card's rows do not show), and only when it has one.
 */
function TouchFacts({
  title,
  touch,
  utmKeys = FIRST_TOUCH_UTM_KEYS,
}: {
  title: string
  touch: AdminTouch
  utmKeys?: readonly (typeof UTM_KEYS)[number][]
}) {
  const utm = utmKeys.flatMap((key) => (touch.utm[key] ? [`${key}=${touch.utm[key]}`] : []))
  // Which click IDs came with it, never their values.
  const clickIds = Object.entries(touch.clickIds).flatMap(([key, value]) => (value ? [key] : []))
  return (
    <div className="admin-stack">
      <h3 className="admin-record-subtitle">{title}</h3>
      <DescriptionList
        items={[
          { term: "Time", value: <When iso={touch.createdAt} /> },
          { term: "Channel", value: <ChannelBadge channel={touch.channel} /> },
          {
            term: "Landing page",
            value: <LandingUrl touch={title.toLowerCase()} url={touch.landingUrl} />,
          },
          { term: "Referrer", value: touch.referrer ? breakable(touch.referrer) : none },
          ...(utm.length ? [{ term: "UTM tags", value: utm.join(", ") }] : []),
          { term: "Click IDs", value: clickIds.length ? clickIds.join(", ") : none },
        ]}
      />
    </div>
  )
}

function Acquisition({ acquisition }: { acquisition: AdminCustomerRecord["acquisition"] }) {
  if (!acquisition) {
    return (
      <p className="admin-note">
        No acquisition recorded. Members from before signup tracking have none.
      </p>
    )
  }
  const { firstTouch, lastTouch } = acquisition
  return (
    <div className="admin-record-column">
      <DescriptionList
        items={[
          { term: "Channel", value: <ChannelBadge channel={acquisition.channel} /> },
          { term: "Source", value: acquisition.source ?? none },
          { term: "Medium", value: acquisition.medium ?? none },
          { term: "Campaign", value: acquisition.campaign ?? none },
        ]}
      />
      {firstTouch && firstTouch.id === lastTouch?.id ? (
        <TouchFacts title="First and last touch" touch={firstTouch} />
      ) : (
        <>
          {firstTouch ? <TouchFacts title="First touch" touch={firstTouch} /> : null}
          {lastTouch ? (
            <TouchFacts title="Last touch" touch={lastTouch} utmKeys={UTM_KEYS} />
          ) : null}
        </>
      )}
    </div>
  )
}

/** A record card while it loads: a title bar over `lines` lines. */
function SkeletonCard({ lines }: { lines: number }) {
  return (
    <Card>
      <Skeleton height="1.25rem" width="8rem" />
      <Skeleton lines={lines} />
    </Card>
  )
}

/**
 * The CRM record: who the customer is, where they came from, what they answered and agreed to,
 * their products, and every journey step oldest first. Focus moves to the heading once it loads.
 */
export function AdminCustomerPage() {
  const { id = "" } = useParams()
  const { state } = useLocation()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const query = useQuery({
    queryFn: ({ signal }) => loadAdminCustomer(id, { signal }),
    queryKey: ["admin-customer", id],
  })

  // Once the record, the 404 or the error shows, and again after a Retry that loads it.
  useEffect(() => {
    if (query.status !== "pending") headingRef.current?.focus()
  }, [query.status])

  const back = (
    <Link to={`/dashboard/customers${listSearch(state)}`}>
      <NavIcon name="back" />
      Customers
    </Link>
  )

  if (query.isPending) {
    return (
      <section aria-busy="true" className="admin-stack admin-page">
        <PageHeader back={back} title="Customer" />
        <p className="tny-visually-hidden" role="status">
          Loading customer…
        </p>
        {/* Shaped like the record: two columns of cards. */}
        <div className="admin-record">
          <div className="admin-record-column">
            <SkeletonCard lines={4} />
            <SkeletonCard lines={3} />
          </div>
          <div className="admin-record-column">
            <SkeletonCard lines={5} />
            <SkeletonCard lines={2} />
          </div>
        </div>
      </section>
    )
  }

  if (query.isError) {
    const { error } = query
    if (error instanceof AdminApiError && error.status === 404) {
      return (
        <section className="admin-stack admin-page">
          <PageHeader back={back} headingRef={headingRef} title="Customer not found" />
          <EmptyState
            action={
              <Link
                className="tny-button tny-button-secondary"
                to={`/dashboard/customers${listSearch(state)}`}
              >
                Back to Customers
              </Link>
            }
            description="Check the link, or find the customer in your list."
            title="No customer has this link"
          />
        </section>
      )
    }
    return (
      <section className="admin-stack admin-page">
        <PageHeader back={back} headingRef={headingRef} title="Customer" />
        <LoadError
          detail={errorDetail(error)}
          message="We couldn't load this customer"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </section>
    )
  }

  const { customer: record, timeline } = query.data
  const name = fullName(record)
  const avatar = discordAvatarSrc(record.discordAvatarUrl)
  const journey = [...timeline]
    .sort(compareJourneyEvents)
    .map((event) => ({ at: event.at, id: event.id, ...describeJourneyEvent(event) }))

  return (
    <section className="admin-stack admin-page">
      <PageHeader
        back={back}
        description={
          <span className="admin-record-meta">
            <span className="admin-record-badges">
              {record.channel ? <ChannelBadge channel={record.channel} /> : null}
              {record.joinedAt ? (
                <Badge tone="success">Joined</Badge>
              ) : (
                <Badge>Not joined yet</Badge>
              )}
            </span>
            <span className="admin-record-signed">
              Signed up <When iso={record.signedUpAt} />
            </span>
            {/* Member tracking only: a member who left is marked, and the record stays. */}
            {record.inServer === false && record.leftServerAt ? (
              <span className="admin-record-signed">
                Left the server on <When iso={record.leftServerAt} />
              </span>
            ) : null}
          </span>
        }
        headingRef={headingRef}
        media={<Avatar name={name} size="lg" src={avatar} />}
        title={name}
      />
      {/* In the order a phone shows them, one column; from 64rem app.css places them in two that
          each keep this order: Identity and Acquisition, then Journey, Discord, Answers, Products
          and Consent. */}
      <div className="admin-record admin-record-cards">
        <Card title="Identity">
          <DescriptionList
            items={[
              { term: "Name", value: name },
              { term: "Email", value: breakable(record.email) },
              { term: "Phone", value: record.phone ?? none },
              { term: "Country", value: record.country ? countryName(record.country) : none },
            ]}
          />
        </Card>
        <Card title="Journey">
          <Timeline
            emptyLabel="No journey events yet."
            items={journey}
            label="Journey"
            locale={LOCALE}
          />
        </Card>
        <Card title="Acquisition">
          <Acquisition acquisition={record.acquisition} />
        </Card>
        <Card title="Discord">
          {record.discordId || record.discordUsername ? (
            <DescriptionList
              items={[
                {
                  term: "Username",
                  value: record.discordUsername ? (
                    <span className="admin-customer">
                      {avatar ? (
                        <Avatar name={record.discordUsername} size="sm" src={avatar} />
                      ) : null}
                      {`@${record.discordUsername}`}
                    </span>
                  ) : (
                    none
                  ),
                },
                { term: "Display name", value: record.discordDisplayName ?? none },
                {
                  term: "Discord ID",
                  value: record.discordId ? (
                    <span className="admin-mono">{record.discordId}</span>
                  ) : (
                    none
                  ),
                },
              ]}
            />
          ) : (
            <p className="admin-note">Not linked yet.</p>
          )}
        </Card>
        <Card title="Answers">
          <Answers record={record} />
        </Card>
        <Card title="Products">
          {record.products.length > 0 ? (
            // biome-ignore lint/a11y/noRedundantRoles: Safari drops the list role of a list styled with list-style: none.
            <ul className="admin-record-products" role="list">
              {record.products.map((product) => (
                <li key={product.id}>{product.name}</li>
              ))}
            </ul>
          ) : (
            <p className="admin-note">No products yet.</p>
          )}
        </Card>
        <Card title="Consent">
          {record.consent ? (
            <DescriptionList
              items={[
                {
                  term: "Terms",
                  value: (
                    <>
                      Accepted <When iso={record.consent.terms} />
                      {record.consent.termsIp ? (
                        <>
                          {" "}
                          from <span className="admin-nowrap">{record.consent.termsIp}</span>,{" "}
                        </>
                      ) : (
                        ", IP not recorded, "
                      )}
                      <span className="admin-nowrap">version {record.consent.termsVersion}</span>
                    </>
                  ),
                },
                { term: "Ad pixels", value: record.consent.ads ? "Yes" : "No" },
              ]}
            />
          ) : (
            <p className="admin-note">No consent recorded.</p>
          )}
        </Card>
      </div>
    </section>
  )
}
